/**
 * Mapeia MessageType para código numérico
 */
const messageTypeCodes: Record<string, number> = {
  NewSchool: 10,
  UpdateSchool: 11,
  DeleteSchool: 12,
  NewSection: 20,
  UpdateSection: 21,
  DeleteSection: 22,
  NewTeacher: 30,
  UpdateTeacher: 31,
  DeleteTeacher: 32,
  NewStudent: 40,
  UpdateStudent: 41,
  DeleteStudent: 42,
  NewTeacherRoster: 50,
  DeleteTeacherRoster: 52,
  NewStudentEnrollment: 60,
  DeleteStudentEnrollment: 62,
  NewStaff: 70,
  UpdateStaff: 71,
  DeleteStaff: 72,
  NewStaffRoster: 80,
  DeleteStaffRoster: 81,
};

/**
 * Tipo de entidade para categorização (usuários e não-usuários)
 */
export type UserType = 'student' | 'teacher' | 'staff' | 'class' | 'school' | 'unknown';

/**
 * Determina o tipo de entidade principal baseado no messageType
 * Para Roster/Enrollment, extrai o tipo de usuário (não a operação)
 * Para Class/Section/School, retorna o tipo de entidade correspondente
 */
export function getUserTypeFromMessageType(messageType: string | null | undefined): UserType {
  if (!messageType) return 'unknown';
  const type = messageType.toLowerCase();
  if (type.includes('student')) return 'student';
  if (type.includes('teacher')) return 'teacher';
  if (type.includes('staff')) return 'staff';
  if (type.includes('class') || type.includes('section')) return 'class';
  if (type.includes('school')) return 'school';
  return 'unknown';
}

/**
 * Formata o ID do usuário com prefixo maiúsculo
 * @param externalId - ID externo do usuário
 * @param userType - Tipo do usuário (student, teacher, staff)
 * @returns ID formatado (ex: STU_1142265608)
 */
export function formatUserIdWithPrefix(externalId: string | undefined, userType: UserType): string {
  if (!externalId) return '-';

  const prefixMap: Record<UserType, string> = {
    'student': 'STU_',
    'teacher': 'TEA_',
    'staff': 'STA_',
    'class': 'SEC_',
    'school': 'SCH_',
    'unknown': ''
  };

  // Remove prefixo existente se houver e normaliza
  const cleanId = externalId.replace(/^(stu_|tea_|sta_|stf_)/i, '');

  return `${prefixMap[userType]}${cleanId}`;
}

/**
 * Verifica se o messageType envolve uma classe/turma como relacionamento
 * Retorna true para Roster e Enrollment (operações de usuário em turma)
 */
export function operationInvolvesClass(messageType: string | null | undefined): boolean {
  if (!messageType) return false;
  const type = messageType.toLowerCase();
  return type.includes('roster') || type.includes('enrollment');
}

/**
 * Verifica se o messageType é uma operação direta de classe/turma
 * Retorna true para NewClass, UpdateClass, DeleteSection
 */
export function isClassOperation(messageType: string | null | undefined): boolean {
  if (!messageType) return false;
  const type = messageType.toLowerCase();
  return type.includes('class') || type.includes('section');
}

/**
 * Formata o ID da classe com prefixo SEC_
 */
export function formatClassIdWithPrefix(sectionId: string | undefined): string {
  if (!sectionId) return '-';
  const cleanId = sectionId.replace(/^(sec_|cls_)/i, '');
  return `SEC_${cleanId}`;
}

/**
 * Mapeia MessageType para nome de entidade em português
 * FOCO NO USUÁRIO: Roster e Enrollment mostram o tipo de usuário, não a operação
 */
const entityNames: Record<string, string> = {
  // Estudantes - SEMPRE foco no estudante
  NewStudent: 'Estudante',
  UpdateStudent: 'Estudante',
  DeleteStudent: 'Estudante',
  NewStudentEnrollment: 'Estudante',    // Foco no estudante
  DeleteStudentEnrollment: 'Estudante',
  NewEnrollment: 'Estudante',           // Alias antigo
  DeleteEnrollment: 'Estudante',
  // Professores - SEMPRE foco no professor
  NewTeacher: 'Professor',
  UpdateTeacher: 'Professor',
  DeleteTeacher: 'Professor',
  NewTeacherRoster: 'Professor',        // Foco no professor
  DeleteTeacherRoster: 'Professor',
  // Staff - SEMPRE foco no funcionário
  NewStaff: 'Funcionário',
  UpdateStaff: 'Funcionário',
  DeleteStaff: 'Funcionário',
  NewStaffRoster: 'Funcionário',        // Foco no funcionário
  DeleteStaffRoster: 'Funcionário',
  // Entidades não-usuário (mantem igual)
  NewClass: 'Turma',
  UpdateClass: 'Turma',
  DeleteClass: 'Turma',
  NewSection: 'Turma',
  UpdateSection: 'Turma',
  DeleteSection: 'Turma',
  NewSchool: 'Escola',
  UpdateSchool: 'Escola',
  DeleteSchool: 'Escola',
};

/**
 * Mapeia MessageType para tipo de operação em português
 */
const operationTypes: Record<string, string> = {
  NewStudent: 'Novo estudante',
  UpdateStudent: 'Atualização de estudante',
  DeleteStudent: 'Exclusão de estudante',
  NewTeacher: 'Novo professor',
  UpdateTeacher: 'Atualização de professor',
  DeleteTeacher: 'Exclusão de professor',
  NewClass: 'Nova turma',
  UpdateClass: 'Atualização de turma',
  DeleteClass: 'Exclusão de turma',
  NewSection: 'Nova turma', // Backend usa Section
  UpdateSection: 'Atualização de turma',
  DeleteSection: 'Exclusão de turma',
  NewSchool: 'Nova escola',
  UpdateSchool: 'Atualização de escola',
  DeleteSchool: 'Exclusão de escola',
  NewStudentEnrollment: 'Nova matrícula', // Backend usa StudentEnrollment
  DeleteStudentEnrollment: 'Exclusão de matrícula',
  NewEnrollment: 'Nova matrícula', // Alias antigo
  DeleteEnrollment: 'Exclusão de matrícula',
  NewTeacherRoster: 'Novo professor em turma',
  DeleteTeacherRoster: 'Exclusão de professor de turma',
  NewStaff: 'Novo funcionário',
  UpdateStaff: 'Atualização de funcionário',
  DeleteStaff: 'Exclusão de funcionário',
  NewStaffRoster: 'Novo funcionário em turma',
  DeleteStaffRoster: 'Exclusão de funcionário de turma',
};

/**
 * Extrai nome limpo do ExternalId
 * Exemplo: "stu_STU_003_CARLOS" → "CARLOS"
 */
function extractNameFromExternalId(externalId?: string): string | null {
  if (!externalId) return null;

  // Remove prefixos comuns: stu_, tea_, cls_, sch_, etc
  const cleaned = externalId
    .replace(/^(stu|tea|cls|sch|enr|str|stf)_/i, '')  // Remove prefixo inicial
    .replace(/^[A-Z]{3}_\d+_/i, '');  // Remove padrão STU_003_, TEA_001_, etc

  // Se sobrou algo e não é só números, retorna
  if (cleaned && !/^\d+$/.test(cleaned)) {
    return cleaned.replace(/_/g, ' '); // Troca _ por espaço
  }

  return null;
}

/**
 * Formata o título da operação
 * Formato: Entidade: <nome> - Email: <email> - Operação: <tipo de operação>
 * Exemplo: Aluno: Pedro Henrique Almeida - Email: pedro@senacsp.edu.br - Operação: Novo estudante
 */
export function formatOperationTitle(
  messageType: string,
  displayName?: string,
  email?: string,
  externalId?: string
): string {
  const entityName = entityNames[messageType] || 'Entidade';
  const operationType = operationTypes[messageType] || messageType;

  // Tenta obter nome do displayName, depois do externalId, senão usa externalId completo
  let identifier: string;
  if (displayName && displayName !== 'Unknown') {
    identifier = displayName;
  } else {
    const extractedName = extractNameFromExternalId(externalId);
    identifier = extractedName || externalId || 'Sem identificação';
  }

  // Monta o título com email (se disponível)
  let title = `${entityName}: ${identifier}`;

  if (email) {
    title += ` - Email: ${email}`;
  }

  title += ` - Operação: ${operationType}`;

  return title;
}

/**
 * Obtém apenas o nome da entidade
 */
export function getEntityName(messageType: string): string {
  return entityNames[messageType] || 'Entidade';
}

/**
 * Obtém apenas o tipo de operação
 */
export function getOperationType(messageType: string): string {
  return operationTypes[messageType] || messageType;
}

/**
 * Obtém o código numérico do tipo de mensagem
 */
export function getMessageTypeCode(messageType: string): number | null {
  return messageTypeCodes[messageType] || null;
}

/**
 * Formata o UPN com domínio se não tiver
 */
export function formatUpnWithDomain(upn?: string, domain: string = 'senacsp.edu.br'): string {
  if (!upn) return '';
  if (upn.includes('@')) return upn;
  return `${upn}@${domain}`;
}

/**
 * Traduz nomes de operações do sistema para português
 */
export function formatOperation(operation: string): string {
  const operationMap: Record<string, string> = {
    'API_RECEIVED': 'Api Received',
    'QUEUE_PUBLISHED': 'Queue Published',
    'QUEUE_CONSUMED': 'Queue Consumed',
    'WORKER_STARTED': 'Worker Started',
    'WORKER_COMPLETED': 'Worker Completed',
    'GRAPH_CALL_START': 'Graph Call Start',
    'GRAPH_CALL_SUCCESS': 'Graph Call Success',
    'GRAPH_CALL_FAILED': 'Graph Call Failed',
    'GRAPH_CALL_SKIPPED': 'Graph Call Skipped',
    'GRAPH_TEAMS_SUCCESS': 'Graph Teams Success',
    'GRAPH_TEAMS_FAILED': 'Graph Teams Failed',
    'MESSAGE_COMPLETED': 'Message Completed',
    'RETRY_SCHEDULED': 'Retry Agendado',
    'SENT_TO_DLQ': 'Enviado para DLQ',
  };

  return operationMap[operation?.toUpperCase()] || operation;
}
